import {
  getRemainingTicketsFromRateLimit,
  getRemainingTicketsFromTicketingLimit,
} from "../../../../../components/SelectTicketInput/useSelectTicketInput";
import { RateByOccurrenceObject } from "../../../../../gql/gql-types";

import { FullRateError } from "@/types/ticketing";
import { RateOutput } from "@/types/trpc";

export type MinRateCapacityReason = "EventCapacity" | "RateCapacity" | "RateLimitPerPurchase" | "Unknown";

export const calculateMaximumPurchasableTicketsForRate = ({
  rate,
  purchasedNumber,
  totalParticipants,
  remainingTicketingSeats,
  currentRateByOccurrence,
  fullRatesReturnByOrder,
}: {
  rate: RateOutput;
  purchasedNumber: number;
  totalParticipants: number;
  remainingTicketingSeats: number;
  currentRateByOccurrence: RateByOccurrenceObject | undefined;
  fullRatesReturnByOrder: FullRateError[];
}): {
  maximumPurchasableTicketsForRate: number;
  maximumPurchasableTicketsForRateAndEventCapacity: number;
  minRateCapacityReason: MinRateCapacityReason;
} => {
  // 1. Maximum based on event capacity
  const maximumBasedOnEventCapacity = getRemainingTicketsFromTicketingLimit(
    rate,
    purchasedNumber,
    remainingTicketingSeats,
    totalParticipants
  );

  // 2. Maximum based on rate capacity
  const rateRemainingTickets = currentRateByOccurrence?.remainingTickets ?? Infinity;
  const maximumBasedOnRateCapacity = getRemainingTicketsFromRateLimit(
    rate,
    fullRatesReturnByOrder,
    rateRemainingTickets
  );

  // 3. Maximum based on rate's per-order limit
  const maximumBasedOnRateLimit = rate.maximumToBuy ?? Infinity;

  const maximumPurchasableTicketsForRateAndEventCapacity = Math.min(
    maximumBasedOnEventCapacity,
    maximumBasedOnRateCapacity
  );

  // Calculate the maximum purchasable tickets for the rate
  const minValue = Math.min(maximumPurchasableTicketsForRateAndEventCapacity, maximumBasedOnRateLimit);

  return {
    maximumPurchasableTicketsForRate: Math.max(minValue, 0),
    maximumPurchasableTicketsForRateAndEventCapacity: Math.max(maximumPurchasableTicketsForRateAndEventCapacity, 0),
    minRateCapacityReason:
      minValue === maximumBasedOnEventCapacity
        ? "EventCapacity"
        : minValue === maximumBasedOnRateCapacity
          ? "RateCapacity"
          : minValue === maximumBasedOnRateLimit
            ? "RateLimitPerPurchase"
            : "Unknown",
  };
};
